import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Notification } from '@perch-labs/shrike';
import { Item } from 'react-stately';
import { copyFor, routes } from 'config';
import { NOTIFICATION_DISPLAY_TIME } from 'domain/ux';
import { LoadingText } from 'styles';
import { litePlanName } from 'domain/plans';
import { Combobox, itemKey } from 'components/dropdown';
import { useAccountContext } from 'contexts/account';
import { GET_LITE_PLANS } from './plan-switcher-lite.api';

interface Props {
  onClose?: () => void;
  selectedPlanId?: number;
}

const getNotificationCopy = copyFor('toastNotifications');
const getCopy = copyFor('planSwitcher');

export const PlanSwitcherLite = ({ onClose, selectedPlanId }: Props) => {
  const history = useHistory();
  const { addNotification } = useContext(Notification.Context);
  const { selectedAccountId } = useAccountContext();
  const { data, error, loading } = useQuery<
    GetLitePlansQuery,
    GetLitePlansQueryVariables
  >(GET_LITE_PLANS, {
    variables: { accountId: selectedAccountId },
  });

  if (loading)
    return (
      <LoadingText data-testid="plan-switcher-lite-loading">
        {getCopy('loading')}
      </LoadingText>
    );

  const plans = data?.account.customPlans;
  if (error || !plans) return null;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const selectedPlan = plans.find(plan => plan.id === selectedPlanId)!;

  const onSelectionChange = (key: string) => {
    // key = "${id}:${planName}"
    const id = key?.split(':')[0];
    const newSelectedPlan = plans.find(plan => plan.id.toString() === id);
    if (!newSelectedPlan || newSelectedPlan.id === selectedPlan.id) return;
    addNotification({
      displayTime: NOTIFICATION_DISPLAY_TIME,
      message: getNotificationCopy('accountSwitched', {
        name: litePlanName(newSelectedPlan),
      }),
      variant: 'success',
    });
    // send everyone to solar since that exists for all plans
    history.push(routes({ planId: newSelectedPlan.id }).solar);
    if (onClose) onClose();
  };

  return (
    <Combobox
      className="notranslate"
      data-testid="plan-switcher-lite"
      label={getCopy('dropdownLabel')}
      defaultItems={plans}
      selectedKey={
        selectedPlan.id && itemKey(selectedPlan.id, litePlanName(selectedPlan))
      }
      onSelectionChange={onSelectionChange}
    >
      {item => (
        <Item key={itemKey(item.id, litePlanName(item))}>
          {litePlanName(item)}
        </Item>
      )}
    </Combobox>
  );
};
